import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import useInterval from 'use-interval';
import { isLoading } from 'app/utils/utils';
import {
  fetchContributionClassesRolesDDLEffect,
  fetchContributionsListEffect,
} from 'app/store/SongList/effects';
import { useAppDispatch, useAppSelector } from 'app/store';
import { ContributionClassName } from 'types/contribution';
import {
  getCanViewLatestSplit,
  getContributionClassesAndRoles,
  getContributionsListLink,
  getContributionsListLoadingState,
  getCreateContributionLinkSelector,
  getIsContributionsListUpdating,
  getSortedContributions,
} from 'app/store/SongList/selectors';
import { useResource } from 'app/utils/hooks';
import { getIsSubscribedToResource } from 'app/store/WebSockets/selectors';
import { areComponentPropsEqual } from './utils';
import { ContributionsListComponent } from './ContributionsListComponent';

const ContributionsListComponentMemoized = React.memo(
  ContributionsListComponent,
  areComponentPropsEqual
);

export const ContributionsList = () => {
  const { guid } = useParams();

  const dispatch = useAppDispatch();
  const contributionsListLoadingState = useAppSelector(
    getContributionsListLoadingState
  );
  const [
    contributionClassesAndRoles,
    contributionClassesAndRolesLoading,
  ] = useResource(
    fetchContributionClassesRolesDDLEffect,
    getContributionClassesAndRoles
  );

  const canAddContribution = Boolean(
    useAppSelector(getCreateContributionLinkSelector)
  );
  const contributions = useAppSelector(getSortedContributions);
  const canViewLatestSplit = useAppSelector(getCanViewLatestSplit);
  const isSubscribedToSong = useAppSelector(
    getIsSubscribedToResource(guid as string)
  );

  const getAffectSplit = (className: ContributionClassName) =>
    className === ContributionClassName.SONGWRITER && canViewLatestSplit;

  const loading =
    isLoading(contributionsListLoadingState) ||
    contributionClassesAndRolesLoading;

  const pollInterval =
    (useAppSelector(getContributionsListLink)?.pollingInterval || 0) * 1000 ||
    null;
  const isUpdatingContributions = useAppSelector(
    getIsContributionsListUpdating
  );
  const stopPolling = isSubscribedToSong || isUpdatingContributions || loading;
  const pollContributions = () =>
    dispatch(fetchContributionsListEffect({ update: true }));

  useInterval(pollContributions, stopPolling ? null : pollInterval, false);

  useEffect(() => {
    dispatch(fetchContributionsListEffect({}));
  }, [dispatch]);

  return (
    <ContributionsListComponentMemoized
      loading={loading}
      contributions={contributions}
      dispatch={dispatch}
      canAddContribution={canAddContribution}
      contributionClassesAndRoles={contributionClassesAndRoles}
      getAffectSplit={getAffectSplit}
    />
  );
};
